import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TEAM_IMAGE = '/team/founders-diptych.jpg';
const ALEX_PORTRAIT = '/team/alex-portrait.jpg';
const FERNANDA_PORTRAIT = '/team/fernanda-portrait.jpg';

const COPY = {
  pt: {
    eyebrow: 'Founders · Your Broker Portugal',
    heroTitle: 'Fundada por quem viveu',
    heroTitleEm: 'o mercado dos dois lados.',
    heroLead:
      'Quatro décadas no mercado imobiliário transatlântico. Duas gerações. Uma única convicção: o comprador internacional em Portugal merece a profundidade de mercado e a sofisticação editorial que encontra em Nova Iorque, Londres ou São Paulo.',

    manifestoEyebrow: 'Manifesto',
    manifestoTitle: 'O que nos distingue não é o que vendemos.',
    manifestoTitleEm: 'É a quem servimos.',
    manifestoBody1:
      'Quase todas as estruturas imobiliárias em Portugal trabalham para o vendedor. É natural — é quem paga. Mas o comprador internacional fica do outro lado da mesa, sem representação real, sem alguém que negoceie o seu interesse, sem critério para filtrar o ruído.',
    manifestoBody2:
      'A Your Broker Portugal nasce desta lacuna. Representamos exclusivamente o comprador. Não temos imóveis para vender. Não temos quota para cumprir. A nossa única referência é o seu projeto.',
    manifestoQuote:
      'O nosso compromisso é estar do seu lado da mesa — sempre, sem exceções, sem agenda comercial paralela.',

    foundersEyebrow: 'Os fundadores',
    foundersTitle: 'Alex Kühne & Fernanda Stelzer.',
    foundersIntro:
      'Pai e filha. Uma operação luso-brasileira fundada em 2021, ancorada em quatro décadas de mercado imobiliário no Brasil e dezoito anos de arquitectura de luxo em São Paulo.',
    alexName: 'Alex Kühne',
    alexRole: 'Co-fundador · Consultor Principal',
    alexParas: [
      'Alex Kühne iniciou a sua carreira no mercado imobiliário em 1985 — numa época em que a tecnologia ainda não tinha chegado ao sector. Em 1998, fundou a Imobiliária Casa da Praia no Guarujá e lançou um dos primeiros sites de imóveis do Brasil, antecipando em anos a transformação digital que o sector só viria a viver muito depois.',
      'Durante quatro décadas, especializou-se nas áreas mais exclusivas do litoral paulista — Iporanga, São Pedro e Taguaíba — participando directamente em cerca de 70% das transacções no Condomínio Iporanga, um dos mercados mais selectivos do Brasil, onde confiança, discrição e profundo conhecimento territorial não são diferenciais: são pré-requisitos.',
      'Em 2021, expandiu a sua actuação para a Europa com a cofundação da Your Broker Portugal, ao lado da sua filha Fernanda. A decisão não foi uma mudança de rumo — foi a extensão natural de uma carreira construída sobre um princípio único: representar o comprador com total independência, sem conflito de interesses, e com o conhecimento que só décadas de mercado real conferem.',
      'Hoje conecta dois continentes, levando a compradores internacionais em Portugal o mesmo rigor, a mesma discrição e a mesma inteligência de mercado que definiram a sua reputação no Brasil.',
    ],
    alexAcademic: 'Activo no mercado imobiliário de luxo desde 1985. CRECI 46.673 · AMI 19715.',
    ferName: 'Fernanda Stelzer',
    ferRole: 'Co-fundadora · Experiência do Cliente & Relocation Advisory',
    ferParas: [
      'Fernanda traz para a Your Broker Portugal uma combinação rara que poucos consultores no mercado possuem: 18 anos como arquitecta de luxo em São Paulo e quase cinco anos representando exclusivamente compradores internacionais em Portugal.',
      'A sua carreira na arquitectura — que incluiu passagens pelo Studio Arthur Casas e a cofundação do seu próprio atelier, Stelzer Zebini Arquitetos, com 115 projectos residenciais de alto padrão concluídos — dá-lhe um olhar que vai muito além da transação imobiliária. Onde a maioria dos consultores vê um imóvel, Fernanda vê estrutura, proporção, potencial e os custos invisíveis que só um arquitecto consegue identificar antes de assinar.',
      'Desde a cofundação da Your Broker Portugal em 2021, dedica-se inteiramente à experiência do comprador internacional: compreender o que cada cliente verdadeiramente precisa de uma vida em Portugal, traduzir essa visão num briefing de pesquisa preciso e acompanhá-lo em cada etapa — da primeira conversa ao momento em que se sente em casa.',
      'Brasileira de nascimento, residente em Portugal, e profundamente familiarizada com as duas culturas, Fernanda tem uma sensibilidade particular para a jornada que compradores brasileiros e internacionais percorrem ao escolher Portugal — não apenas como investimento, mas como lugar para viver.',
    ],
    ferAcademic: 'Bacharelado em Arquitectura e Urbanismo, Universidade Católica de Santos, 2004–2008.',

    differenceEyebrow: 'O modelo',
    differenceTitle: 'Buying agent. Não imobiliária.',
    differenceLead:
      'A diferença é mais profunda do que o nome. É estrutural.',
    differenceCols: [
      {
        title: 'Imobiliária tradicional',
        items: [
          'Representa o vendedor',
          'Limitada à sua própria carteira',
          'Mostra o que tem para vender',
          'Interesse em fechar rapidamente',
          'Conflito estrutural inerente',
        ],
      },
      {
        title: 'Your Broker Portugal',
        items: [
          'Representa exclusivamente o comprador',
          'Acesso a todo o mercado, on e off-market',
          'Filtra com critério, em vez de promover',
          'Interesse em encontrar o ajuste certo',
          'Sem custo para o comprador',
        ],
      },
    ],

    valuesEyebrow: 'Os nossos princípios',
    valuesTitle: 'Quatro princípios não negociáveis.',
    values: [
      {
        num: '01',
        title: 'Discrição',
        body: 'A sua identidade, o seu orçamento, os seus motivos. Nada chega a terceiros sem o seu consentimento explícito.',
      },
      {
        num: '02',
        title: 'Independência',
        body: 'Não trabalhamos com o "advogado do vendedor". Não fechamos negócios para cumprir metas. Não temos imóveis para defender.',
      },
      {
        num: '03',
        title: 'Critério',
        body: 'Três a quatro oportunidades por mandato. Filtradas com rigor. Sem catálogos, sem fadiga, sem ruído.',
      },
      {
        num: '04',
        title: 'Profundidade',
        body: 'Acompanhamos cada etapa — do NIF à escritura, do briefing à entrega de chaves. O nível de envolvimento de um private banker, aplicado ao imobiliário.',
      },
    ],

    journeyEyebrow: 'Trajetória',
    journeyTitle: 'Sete países, dois continentes, uma firma.',
    journey: [
      { year: '1989', label: 'Início', body: 'Alex Kühne começa a sua trajetória no real estate residencial em São Paulo.' },
      { year: '2007', label: 'Expansão', body: 'Operações em Miami, Lisboa e Berlim. Primeira representação de compradores europeus no Brasil.' },
      { year: '2015', label: 'Portugal remoto', body: 'Início da atuação no mercado português a partir do Brasil, servindo clientes que procuravam diversificar para a Europa.' },
      { year: '2019', label: 'Fernanda integra', body: 'Fernanda Stelzer junta-se à operação após formação em ciências económicas e dois anos em Berlim.' },
      { year: '2021', label: 'Fundação em Lisboa', body: 'Abertura oficial da Your Broker Portugal. Mudança definitiva da estrutura para Lisboa.' },
      { year: '2026', label: 'Hoje', body: 'Mais de 70 mandatos concluídos para clientes em sete países. Operação sediada em Lisboa, com cobertura nacional.' },
    ],

    closingTitle: 'Conhecer-nos é o primeiro passo.',
    closingBody:
      'Antes de qualquer mandato, há uma conversa privada. Trinta minutos. Sem agenda comercial. Conhecemos o seu projeto e indicamos honestamente se somos o parceiro certo.',
    closingCta: 'Iniciar conversa',
  },
  en: {
    eyebrow: 'Founders · Your Broker Portugal',
    heroTitle: 'Founded by those who lived',
    heroTitleEm: 'the market from both sides.',
    heroLead:
      'Four decades in the transatlantic real estate market. Two generations. One single conviction: the international buyer in Portugal deserves the market depth and editorial sophistication they find in New York, London or São Paulo.',

    manifestoEyebrow: 'Manifesto',
    manifestoTitle: 'What sets us apart is not what we sell.',
    manifestoTitleEm: 'It is who we serve.',
    manifestoBody1:
      'Almost every real estate structure in Portugal works for the seller. Naturally — it is who pays. But the international buyer sits on the other side of the table, without real representation, without someone negotiating in their interest, without criteria to filter the noise.',
    manifestoBody2:
      'Your Broker Portugal was born from this gap. We represent the buyer exclusively. We have no properties to sell. We have no quota to meet. Our only reference is your project.',
    manifestoQuote:
      'Our commitment is to remain on your side of the table — always, without exception, with no parallel commercial agenda.',

    foundersEyebrow: 'The founders',
    foundersTitle: 'Alex Kühne & Fernanda Stelzer.',
    foundersIntro:
      'Father and daughter. A Luso-Brazilian operation founded in 2021, anchored in four decades of Brazilian real estate and eighteen years of luxury architecture in São Paulo.',
    alexName: 'Alex Kühne',
    alexRole: 'Co-founder · Principal Advisor',
    alexParas: [
      'Alex Kühne began his career in real estate in 1985 — at a time when technology had not yet reached the sector. In 1998, he founded Imobiliária Casa da Praia in Guarujá and launched one of the first real estate websites in Brazil, anticipating by years the digital transformation the sector would only undergo much later.',
      'Over four decades, he specialised in the most exclusive areas of the São Paulo coastline — Iporanga, São Pedro and Taguaíba — directly participating in approximately 70% of transactions in Condomínio Iporanga, one of the most selective markets in Brazil, where trust, discretion and deep territorial knowledge are not differentiators: they are prerequisites.',
      'In 2021, he expanded his practice to Europe by co-founding Your Broker Portugal alongside his daughter Fernanda. The decision was not a change of direction — it was the natural extension of a career built on a single principle: to represent the buyer with total independence, free of conflicts of interest, and with the knowledge that only decades of real market experience confer.',
      'Today he connects two continents, bringing to international buyers in Portugal the same rigour, the same discretion and the same market intelligence that defined his reputation in Brazil.',
    ],
    alexAcademic: 'Active in luxury real estate since 1985. CRECI 46,673 · AMI 19715.',
    ferName: 'Fernanda Stelzer',
    ferRole: 'Co-founder · Client Experience & Relocation Advisory',
    ferParas: [
      'Fernanda brings to Your Broker Portugal a rare combination that few consultants in the market possess: 18 years as a luxury architect in São Paulo and nearly five years representing international buyers in Portugal exclusively.',
      'Her architecture career — which included a tenure at Studio Arthur Casas and the co-founding of her own practice, Stelzer Zebini Arquitetos, with 115 high-end residential projects completed — gives her a perspective that extends far beyond the real estate transaction. Where most consultants see a property, Fernanda sees structure, proportion, potential and the invisible costs that only an architect can identify before signing.',
      'Since co-founding Your Broker Portugal in 2021, she has dedicated herself entirely to the international buyer\'s experience: understanding what each client truly needs from a life in Portugal, translating that vision into a precise search briefing, and accompanying them through every stage — from the first conversation to the moment they feel at home.',
      'Brazilian by birth, resident in Portugal, and deeply familiar with both cultures, Fernanda has a particular sensitivity to the journey that Brazilian and international buyers walk when choosing Portugal — not merely as an investment, but as a place to live.',
    ],
    ferAcademic: 'Bachelor\'s in Architecture and Urbanism, Universidade Católica de Santos, 2004–2008.',

    differenceEyebrow: 'The model',
    differenceTitle: 'Buying agent. Not real estate agency.',
    differenceLead: 'The difference runs deeper than the name. It is structural.',
    differenceCols: [
      {
        title: 'Traditional agency',
        items: [
          'Represents the seller',
          'Limited to its own portfolio',
          'Shows what it has to sell',
          'Interest in closing quickly',
          'Inherent structural conflict',
        ],
      },
      {
        title: 'Your Broker Portugal',
        items: [
          'Represents the buyer exclusively',
          'Access to the entire market, on and off-market',
          'Filters with criteria, rather than promotes',
          'Interest in finding the right fit',
          'At no cost to the buyer',
        ],
      },
    ],

    valuesEyebrow: 'Our principles',
    valuesTitle: 'Four non-negotiable principles.',
    values: [
      {
        num: '01',
        title: 'Discretion',
        body: 'Your identity, your budget, your motives. Nothing reaches third parties without your explicit consent.',
      },
      {
        num: '02',
        title: 'Independence',
        body: 'We do not work with the "seller\'s lawyer". We do not close deals to meet quotas. We have no properties to defend.',
      },
      {
        num: '03',
        title: 'Criteria',
        body: 'Three to four opportunities per mandate. Filtered with rigour. No catalogues, no fatigue, no noise.',
      },
      {
        num: '04',
        title: 'Depth',
        body: 'We accompany every step — from NIF to deed, from briefing to handover. The level of involvement of a private banker, applied to real estate.',
      },
    ],

    journeyEyebrow: 'Trajectory',
    journeyTitle: 'Seven countries, two continents, one firm.',
    journey: [
      { year: '1989', label: 'Beginning', body: 'Alex Kühne begins his trajectory in residential real estate in São Paulo.' },
      { year: '2007', label: 'Expansion', body: 'Operations in Miami, Lisbon and Berlin. First representation of European buyers in Brazil.' },
      { year: '2015', label: 'Portugal remote', body: 'Beginning of activity in the Portuguese market from Brazil, serving clients seeking European diversification.' },
      { year: '2019', label: 'Fernanda joins', body: 'Fernanda Stelzer joins the operation after a degree in economics and two years in Berlin.' },
      { year: '2021', label: 'Founded in Lisbon', body: 'Official opening of Your Broker Portugal. Definitive move of the structure to Lisbon.' },
      { year: '2026', label: 'Today', body: 'More than 70 mandates completed for clients in seven countries. Operation based in Lisbon, with national coverage.' },
    ],

    closingTitle: 'Knowing us is the first step.',
    closingBody:
      'Before any mandate, there is a private conversation. Thirty minutes. No commercial agenda. We learn your project and tell you honestly whether we are the right partner.',
    closingCta: 'Begin a conversation',
  },
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const SobrePage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const { title: seoTitle, description: seoDescription } = usePageSEO('sobre', language);
  const ref = useReveal();
  const [homeContent, setHomeContent] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} url="https://yourbrokerportugal.com/sobre" />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
      `}</style>

      <Header companyName={homeContent?.headerFooter?.companyName} logo={homeContent?.headerFooter?.logo} />

      <main ref={ref}>
        {/* HERO — large founder image left, statement right */}
        <section className="pt-28 md:pt-32 pb-20 md:pb-28 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="rv lg:col-span-5">
              <div className="aspect-square overflow-hidden bg-editorial-sand">
                <img
                  src={TEAM_IMAGE}
                  alt="Alex Kühne & Fernanda Stelzer"
                  className="w-full h-full object-cover"
                  style={{ filter: 'grayscale(0.08) contrast(1.02)' }}
                  data-testid="about-team-image"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rv">
                <Eyebrow>{copy.eyebrow}</Eyebrow>
              </div>
              <h1
                className="rv text-editorial-charcoal font-light mt-7 mb-9"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 4.4vw, 4rem)', lineHeight: '1.08' }}
                data-testid="about-hero-title"
              >
                {copy.heroTitle}{' '}
                <em className="italic text-editorial-bronze">{copy.heroTitleEm}</em>
              </h1>
              <p
                className="rv text-editorial-muted font-light max-w-xl"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.heroLead}
              </p>
            </div>
          </div>
        </section>

        {/* MANIFESTO */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-sand/50">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.manifestoEyebrow}</Eyebrow>
            </div>
            <h2
              className="rv text-editorial-charcoal font-light mt-7 max-w-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
            >
              {copy.manifestoTitle}{' '}
              <em className="italic text-editorial-bronze">{copy.manifestoTitleEm}</em>
            </h2>
            <div
              className="rv max-w-2xl mt-12 space-y-7 text-editorial-muted font-light"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              <p>{copy.manifestoBody1}</p>
              <p>{copy.manifestoBody2}</p>
            </div>
            <blockquote
              className="rv mt-14 max-w-2xl pl-7 italic text-editorial-charcoal-soft"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.35rem',
                fontWeight: 300,
                lineHeight: '1.6',
                borderLeft: '2px solid #b8955a',
              }}
            >
              {copy.manifestoQuote}
            </blockquote>
          </div>
        </section>

        {/* FOUNDERS DETAIL */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-mineral">
          <div className="max-w-6xl mx-auto">
            <div className="rv max-w-3xl mb-14">
              <Eyebrow>{copy.foundersEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7 mb-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.foundersTitle}
              </h2>
              <p
                className="text-editorial-muted font-light"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.foundersIntro}
              </p>
            </div>

            <div className="rv grid grid-cols-1 md:grid-cols-2 gap-px bg-editorial-sand-deep items-stretch">
              {[
                { name: copy.alexName, role: copy.alexRole, paras: copy.alexParas, academic: copy.alexAcademic, portrait: ALEX_PORTRAIT },
                { name: copy.ferName, role: copy.ferRole, paras: copy.ferParas, academic: copy.ferAcademic, portrait: FERNANDA_PORTRAIT },
              ].map((p) => (
                <article
                  key={p.name}
                  className="bg-editorial-mineral hover:bg-editorial-bone transition-colors duration-500 p-10 md:p-12 flex flex-col"
                  data-testid={`founder-${p.name.split(' ')[0]}`}
                >
                  <div className="mb-7 w-28 h-28 overflow-hidden bg-editorial-sand">
                    <img
                      src={p.portrait}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(0.1) contrast(1.02)', objectPosition: 'center 22%' }}
                      data-testid={`founder-portrait-${p.name.split(' ')[0]}`}
                    />
                  </div>
                  <h3
                    className="text-editorial-charcoal font-light mb-2"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.85rem', lineHeight: '1.15' }}
                  >
                    {p.name}
                  </h3>
                  <p
                    className="text-editorial-bronze mb-6"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    {p.role}
                  </p>
                  <div className="space-y-5 flex-1">
                    {p.paras.map((para, idx) => (
                      <p
                        key={idx}
                        className="text-editorial-muted font-light"
                        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.92' }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                  {p.academic && (
                    <p
                      className="text-editorial-muted/70 italic mt-8 pt-5 border-t"
                      style={{
                        fontFamily: 'Cormorant Garamond, serif',
                        fontSize: '0.95rem',
                        lineHeight: '1.55',
                        fontWeight: 300,
                        borderColor: 'rgba(28,28,26,0.08)',
                      }}
                    >
                      {p.academic}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* MODEL — Comparative editorial style */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-6xl mx-auto">
            <div className="rv max-w-3xl mb-14">
              <Eyebrow dark>{copy.differenceEyebrow}</Eyebrow>
              <h2
                className="text-editorial-bone font-light mt-7 mb-5"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.differenceTitle}
              </h2>
              <p
                className="text-editorial-bone/55 font-light"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.differenceLead}
              </p>
            </div>

            <div className="rv grid grid-cols-1 md:grid-cols-2 gap-px bg-editorial-bone/10">
              {copy.differenceCols.map((col, i) => (
                <div
                  key={col.title}
                  className={`p-10 md:p-12 ${i === 0 ? 'bg-editorial-charcoal-soft' : 'bg-editorial-charcoal'}`}
                  data-testid={`difference-col-${i}`}
                >
                  <p
                    className={`mb-7 ${i === 0 ? 'text-editorial-bone/40' : 'text-editorial-bronze-lt'}`}
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.6rem', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    {i === 0 ? '— Tradicional —' : '— Your Broker —'}
                  </p>
                  <h3
                    className={`font-light mb-8 ${i === 0 ? 'text-editorial-bone/65' : 'text-editorial-bone'}`}
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', lineHeight: '1.2' }}
                  >
                    {col.title}
                  </h3>
                  <ul className="space-y-4">
                    {col.items.map((item, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start gap-4 ${i === 0 ? 'text-editorial-bone/45' : 'text-editorial-bone/82'}`}
                        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', fontWeight: 300, lineHeight: 1.7 }}
                      >
                        <span className={`mt-2 block w-3 h-px flex-shrink-0 ${i === 0 ? 'bg-editorial-bone/25' : 'bg-editorial-bronze-lt'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv max-w-3xl mb-14">
              <Eyebrow>{copy.valuesEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.valuesTitle}
              </h2>
            </div>

            <div>
              {copy.values.map((v) => (
                <article
                  key={v.num}
                  className="rv grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-12 border-b"
                  style={{ borderColor: 'rgba(28,28,26,0.08)' }}
                  data-testid={`value-${v.num}`}
                >
                  <div className="col-span-12 md:col-span-2">
                    <span
                      className="text-editorial-sand-deep font-light"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3.4rem', lineHeight: 1 }}
                    >
                      {v.num}
                    </span>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <h3
                      className="text-editorial-charcoal font-normal"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.18' }}
                    >
                      {v.title}
                    </h3>
                  </div>
                  <div
                    className="col-span-12 md:col-span-7 text-editorial-muted font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.92' }}
                  >
                    {v.body}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* JOURNEY — section removed (was holding inaccurate dates / fictional milestones). 
            The new Founders bios now carry the verified timeline (1985 · 1998 · 2008 · 2021). */}

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rv">
              <h2
                className="text-editorial-bone font-light mb-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.6vw, 3.2rem)', lineHeight: '1.12' }}
              >
                {copy.closingTitle}
              </h2>
              <p
                className="text-editorial-bone/55 max-w-2xl mx-auto font-light mb-12"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.closingBody}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-center">
                <a
                  href={buildWhatsAppUrl('advisory', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    borderRadius: 0,
                  }}
                  data-testid="about-closing-cta-whatsapp"
                >
                  {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
                </a>
                <Link
                  to={localizedPath('/contact')}
                  className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  data-testid="about-closing-cta"
                >
                  {language === 'pt' ? 'Ou enviar pedido privado' : 'Or send a private enquiry'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default SobrePage;
